import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import type { EventComposition, EventDetail } from '../../lib/events';
import CountsEditor from './CountsEditor';
import type { SimplePanelProps } from './adapt';

const SHARE_TITLE = { role: 'Who attended', identity: 'Self-identified' } as const;

function Composition({ composition }: { composition: EventComposition | null }) {
  if (!composition) return null;
  return (
    <div className="mt-5 pt-4 border-t border-line text-table">
      {(['role', 'identity'] as const).map((group) => {
        const shares = composition.shares.filter((s) => s.group === group);
        return shares.length ? (
          <div key={group} className="mb-3">
            <div className="font-semibold text-small text-ink-2 mb-1">{SHARE_TITLE[group]}</div>
            <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {shares.map((s) => (
                <li key={s.label}>
                  <div className="font-semibold text-[22px] leading-none">{s.percent}%</div>
                  <div className="text-small text-ink-2">{s.label}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : null;
      })}
      {composition.source ? (
        <p className="text-small text-ink-2">Percentages as reported: {composition.source}</p>
      ) : null}
    </div>
  );
}

/** Attendance recorded as numbers, for an event without an attendee list (e.g. a past regional meeting). */
export default function CountsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  if (!q.data || (!q.data.event.counts && (!canEdit || q.data.event.kind === 'national'))) return null;
  return (
    <Panel>
      <PanelHeader title="Attendance as numbers" note="the headcount: people named elsewhere count inside it" />
      <PanelBody>
        <CountsEditor eventId={eventId} counts={q.data.event.counts} canEdit={canEdit} />
        <Composition composition={q.data.event.composition} />
      </PanelBody>
    </Panel>
  );
}
